import { useState } from "react";
import { ChevronRight, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TableScrollContainer from "@/components/ui/table-scroll-container";
import { Client } from "@/features/clients/types/clients";
import { track } from "@/utils/mixpanel";

import EditClientDialog from "./EditClientDialog";

interface ClientsTableProps {
  clients: Client[];
  isLoading?: boolean;
}

const getLeadSourceBadge = (client: Client) => {
  const color =
    client.referredBy === "stack"
      ? "purple"
      : client.referredBy === "mentor" || client.isMentorLead
        ? "blue"
        : "green";
  const label =
    client.referredBy === "stack"
      ? "Stack"
      : client.referredBy === "mentor" || client.isMentorLead
        ? "Mentor"
        : "Own";

  return (
    <Badge
      variant="opaque"
      color={color}
      className="whitespace-nowrap px-[6px] py-[2px] text-[12px] font-semibold"
    >
      {label}
    </Badge>
  );
};

const ClientsTable = ({ clients, isLoading }: ClientsTableProps) => {
  const [editingClient, setEditingClient] = useState<Client | null>(null);

  const handleRowClick = (client: Client) => {
    setEditingClient(client);
    // Track client click
    track("click_client", {
      client_id: client.id,
      email: client.email,
      company: client.company,
    });
  };

  const handleCloseEdit = () => {
    setEditingClient(null);
  };

  // Make sure clients is defined and is an array before using it
  const safeClients = Array.isArray(clients) ? clients : [];

  return (
    <>
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="flex items-center gap-4">
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      ) : safeClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Users className="h-5 w-5 text-[#767677]" />
          <div className="mt-2 text-[#767677] text-sm font-semibold">
            No clients yet
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="flex flex-col gap-3 sm:hidden">
            {safeClients.map((client) => (
              <div
                key={client.id}
                className="bg-white rounded-xl p-4 border border-[#e8e8e8] cursor-pointer hover:border-[#d0d0d0] transition-colors"
                onClick={() => handleRowClick(client)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[14px] font-semibold text-[#1e1e24] truncate">
                        {client.name}
                      </span>
                      {getLeadSourceBadge(client)}
                    </div>
                    {client.company && (
                      <p className="text-[13px] text-[#767677] truncate">
                        {client.company}
                      </p>
                    )}
                    <p className="text-[13px] text-[#767677] truncate mt-0.5">
                      {client.email}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#767677] flex-shrink-0 mt-0.5" />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block">
            <TableScrollContainer minWidth="500px">
              <Table bordered={false}>
                <TableHeader className="bg-transparent">
                  <TableRow>
                    <TableHead className="min-w-[120px] px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Name
                    </TableHead>
                    <TableHead className="min-w-[140px] px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Company
                    </TableHead>
                    <TableHead className="min-w-[80px] px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Lead source
                    </TableHead>
                    <TableHead className="min-w-[180px] px-3 py-1.5 text-[10px] font-semibold tracking-[-0.4px] text-[#767677]">
                      Email
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {safeClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-sds-neutral-00 transition-colors text-[12px] font-semibold"
                      onClick={() => handleRowClick(client)}
                    >
                      <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                        {client.name}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                        {client.company || "-"}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] font-semibold">
                        {getLeadSourceBadge(client)}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-[12px] tracking-[-0.48px] text-[#494949] font-semibold">
                        {client.email}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScrollContainer>
          </div>
        </>
      )}

      {editingClient && (
        <EditClientDialog
          client={editingClient}
          isOpen={!!editingClient}
          onClose={handleCloseEdit}
          onUpdate={() => {}}
        />
      )}
    </>
  );
};

export default ClientsTable;
